import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo, useState } from 'react';
import type { SummaryModeSetting } from '@/components/summarizer/summary-metadata';
import { canonicalizeModelId, getModelDefinition, PROVIDER_DISPLAY_NAMES } from '@/constants/ai';
import { canonicalizeSummaryModel, DEFAULT_SUMMARY_LENGTH_INDEX, type ModelOption } from '@/lib/link-preview/summary-options';
export interface SummaryBaseState {
  customPrompt: string;
  summaryLengthIndex: number;
  summaryMode: SummaryModeSetting;
  summaryModel: ModelOption;
  summarySearchEnabled: boolean;
}
export interface SummaryDraftState {
  customPrompt: string;
  summaryLengthIndex: number;
  summaryMode: SummaryModeSetting;
  summaryModel: ModelOption;
  summarySearchEnabled: boolean;
}
export interface SummarySettingsMetadata {
  canonicalDraftModel: string;
  liveSearchProviderName: string | null;
  providerHandlesVideo: boolean;
  settingsOpen: boolean;
  supportsSummarySearch: boolean;
}
export interface SummarySettingsActions {
  closeSettings: () => void;
  commitDrafts: () => {
    summaryLengthIndex: number;
    summaryModel: ModelOption;
    customPrompt: string;
    summarySearchEnabled: boolean;
    summaryMode: SummaryModeSetting;
  };
  openSettings: () => void;
  setCustomPrompt: (value: string) => void;
  setDraftCustomPrompt: (value: string) => void;
  setDraftModel: (value: ModelOption) => void;
  setDraftSummaryLengthIndex: (value: number) => void;
  setDraftSummaryMode: (value: SummaryModeSetting) => void;
  setDraftSummarySearchEnabled: (value: boolean) => void;
  setSummaryLengthIndex: (value: number) => void;
  setSummaryMode: (value: SummaryModeSetting) => void;
  setSummaryModel: (value: ModelOption) => void;
  setSummarySearchEnabled: (value: boolean) => void;
}
export interface UseSummarySettingsResult {
  actions: SummarySettingsActions;
  base: SummaryBaseState;
  drafts: SummaryDraftState;
  metadata: SummarySettingsMetadata;
}
const providerSupportsSummarySearch = (provider: string | null | undefined) => provider === 'xai' || provider === 'openai' || provider === 'anthropic' || provider === 'google';
export function useSummarySettings() {
  const $ = _c(50);
  const [customPrompt, setCustomPrompt] = useState("");
  const [summaryLengthIndex, setSummaryLengthIndex] = useState(DEFAULT_SUMMARY_LENGTH_INDEX);
  const [summaryModel, setSummaryModel] = useState("model.reasoning.fast");
  const [summarySearchPreference, setSummarySearchPreference] = useState(true);
  const [summaryMode, setSummaryMode] = useState("summary");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [draftSummaryLengthIndex, setDraftSummaryLengthIndex] = useState(summaryLengthIndex);
  const [draftModel, setDraftModel] = useState(summaryModel);
  const [draftCustomPrompt, setDraftCustomPrompt] = useState("");
  const [draftSummarySearchPreference, setDraftSummarySearchPreference] = useState(summarySearchPreference);
  const [draftSummaryMode, setDraftSummaryMode] = useState(summaryMode);
  let t0;
  if ($[0] !== summaryModel) {
    t0 = canonicalizeSummaryModel(summaryModel);
    $[0] = summaryModel;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const canonicalSummaryModel = t0;
  let t1;
  if ($[2] !== draftModel) {
    t1 = canonicalizeSummaryModel(draftModel);
    $[2] = draftModel;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const canonicalDraftModel = t1;
  let t2;
  if ($[4] !== canonicalSummaryModel) {
    const canonicalModelId = canonicalizeModelId(canonicalSummaryModel);
    t2 = canonicalModelId ? getModelDefinition(canonicalModelId) : undefined;
    $[4] = canonicalSummaryModel;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const summaryModelDefinition = t2;
  const canonicalModelId_0 = canonicalizeModelId(canonicalDraftModel);
  const draftModelDefinition = canonicalModelId_0 ? getModelDefinition(canonicalModelId_0) : undefined;
  const currentSummaryProvider = summaryModelDefinition?.provider ?? null;
  const draftSummaryProvider = draftModelDefinition?.provider ?? null;
  let t3;
  if ($[6] !== currentSummaryProvider) {
    t3 = providerSupportsSummarySearch(currentSummaryProvider);
    $[6] = currentSummaryProvider;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const supportsSummarySearch = t3;
  const draftSupportsSummarySearch = providerSupportsSummarySearch(draftSummaryProvider);
  const liveSearchProviderName = currentSummaryProvider ? PROVIDER_DISPLAY_NAMES[currentSummaryProvider] ?? currentSummaryProvider : null;
  const providerHandlesVideo = currentSummaryProvider === "google";
  const summarySearchEnabled = supportsSummarySearch ? summarySearchPreference : false;
  const draftSummarySearchEnabled = draftSupportsSummarySearch ? draftSummarySearchPreference : false;
  let t4;
  if ($[8] !== customPrompt || $[9] !== summaryLengthIndex || $[10] !== summaryMode || $[11] !== summaryModel || $[12] !== summarySearchPreference) {
    t4 = () => {
      setDraftSummaryLengthIndex(summaryLengthIndex);
      setDraftModel(summaryModel);
      setDraftCustomPrompt(customPrompt);
      setDraftSummarySearchPreference(summarySearchPreference);
      setDraftSummaryMode(summaryMode);
      setSettingsOpen(true);
    };
    $[8] = customPrompt;
    $[9] = summaryLengthIndex;
    $[10] = summaryMode;
    $[11] = summaryModel;
    $[12] = summarySearchPreference;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const openSettings = t4;
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setSettingsOpen(false);
    };
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const closeSettings = t5;
  let t6;
  if ($[15] !== draftCustomPrompt || $[16] !== draftModel || $[17] !== draftSummaryLengthIndex || $[18] !== draftSummaryMode || $[19] !== draftSummarySearchEnabled || $[20] !== draftSummarySearchPreference) {
    t6 = () => {
      const trimmedPrompt = draftCustomPrompt.trim();
      setSummaryLengthIndex(draftSummaryLengthIndex);
      setSummaryModel(draftModel);
      setCustomPrompt(trimmedPrompt);
      setSummarySearchPreference(draftSummarySearchPreference);
      setSummaryMode(draftSummaryMode);
      setSettingsOpen(false);
      return {
        summaryLengthIndex: draftSummaryLengthIndex,
        summaryModel: draftModel,
        customPrompt: trimmedPrompt,
        summarySearchEnabled: draftSummarySearchEnabled,
        summaryMode: draftSummaryMode
      };
    };
    $[15] = draftCustomPrompt;
    $[16] = draftModel;
    $[17] = draftSummaryLengthIndex;
    $[18] = draftSummaryMode;
    $[19] = draftSummarySearchEnabled;
    $[20] = draftSummarySearchPreference;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const commitDrafts = t6;
  let t7;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = enabled => {
      setSummarySearchPreference(previous => previous === enabled ? previous : enabled);
    };
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const updateSummarySearchEnabled = t7;
  let t8;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = enabled_0 => {
      setDraftSummarySearchPreference(previous_0 => previous_0 === enabled_0 ? previous_0 : enabled_0);
    };
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const updateDraftSummarySearchEnabled = t8;
  let t9;
  if ($[24] !== customPrompt || $[25] !== summaryLengthIndex || $[26] !== summaryMode || $[27] !== summaryModel || $[28] !== summarySearchEnabled) {
    t9 = {
      customPrompt,
      summaryLengthIndex,
      summaryModel,
      summarySearchEnabled,
      summaryMode
    };
    $[24] = customPrompt;
    $[25] = summaryLengthIndex;
    $[26] = summaryMode;
    $[27] = summaryModel;
    $[28] = summarySearchEnabled;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== draftCustomPrompt || $[31] !== draftModel || $[32] !== draftSummaryLengthIndex || $[33] !== draftSummaryMode || $[34] !== draftSummarySearchEnabled) {
    t10 = {
      summaryLengthIndex: draftSummaryLengthIndex,
      summaryModel: draftModel,
      customPrompt: draftCustomPrompt,
      summarySearchEnabled: draftSummarySearchEnabled,
      summaryMode: draftSummaryMode
    };
    $[30] = draftCustomPrompt;
    $[31] = draftModel;
    $[32] = draftSummaryLengthIndex;
    $[33] = draftSummaryMode;
    $[34] = draftSummarySearchEnabled;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== canonicalDraftModel || $[37] !== liveSearchProviderName || $[38] !== providerHandlesVideo || $[39] !== settingsOpen || $[40] !== supportsSummarySearch) {
    t11 = {
      settingsOpen,
      supportsSummarySearch,
      liveSearchProviderName,
      providerHandlesVideo,
      canonicalDraftModel
    };
    $[36] = canonicalDraftModel;
    $[37] = liveSearchProviderName;
    $[38] = providerHandlesVideo;
    $[39] = settingsOpen;
    $[40] = supportsSummarySearch;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  let t12;
  if ($[42] !== commitDrafts || $[43] !== openSettings) {
    t12 = {
      setCustomPrompt,
      setSummaryLengthIndex,
      setSummaryModel,
      setSummarySearchEnabled: updateSummarySearchEnabled,
      setSummaryMode,
      setDraftSummaryLengthIndex,
      setDraftModel,
      setDraftCustomPrompt,
      setDraftSummarySearchEnabled: updateDraftSummarySearchEnabled,
      setDraftSummaryMode,
      openSettings,
      closeSettings,
      commitDrafts
    };
    $[42] = commitDrafts;
    $[43] = openSettings;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  let t13;
  if ($[45] !== t10 || $[46] !== t11 || $[47] !== t12 || $[48] !== t9) {
    t13 = {
      base: t9,
      drafts: t10,
      metadata: t11,
      actions: t12
    };
    $[45] = t10;
    $[46] = t11;
    $[47] = t12;
    $[48] = t9;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  return t13;
}